import { createTheme } from "@mui/material/styles";

// Define color palette based on new design colors
const lightPalette = {
  primary: {
    main: "#DFE633", // New primary/highlight color
    light: "#F5F9E8", // Very light green-yellow for input backgrounds
    dark: "#8B9A1A", // Darker variant for headers with white text
    contrastText: "#000000",
  },
  secondary: {
    main: "#5382A6", // New secondary color
    light: "#E8F1F8", // Very light blue for subtle backgrounds
    dark: "#3A5B7A", // Darker blue for contrast
    contrastText: "#ffffff",
  },
  tertiary: {
    main: "#E8F4F8", // Light blue-grey to complement the palette
    light: "#F5F9FB",
    dark: "#B8D4E0", // Muted blue-grey
    contrastText: "#2C3E50",
  },
  background: {
    default: "#ffffff",
    paper: "#ffffff",
    dark: "rgba(0, 0, 0, 0.8)", // For dark overlays
    navigation: "#211c16", // Very dark brown for navigation
    header: "#2a2219",
  },
  text: {
    primary: "#2B2520", // Dark brownish color for better readability (not quite black)
    secondary: "#5A6C7D", // Medium blue-grey for secondary text
    disabled: "#757575",
    navigation: "#ffffff", // White text for dark navigation
    navigationSecondary: "rgba(255, 255, 255, 0.8)", // Slightly transparent white
    darkHeader: "#FDFDFD",
  },
  divider: "#e9e9e9f0",
  grey: {
    50: "#f8f9fa", // Very light grey for page backgrounds
    100: "#f1f3f4", // Light grey for alternating table rows
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
  error: {
    main: "#e14d4d",
    light: "#ff7878",
    dark: "#b71c1c",
  },
  warning: {
    main: "#ff9800",
    light: "#ffb74d",
    dark: "#f57c00",
  },
  info: {
    main: "#2196f3",
    light: "#64b5f6",
    dark: "#1976d2",
  },
  success: {
    main: "#61b561",
    light: "#81c784",
    dark: "#388e3c",
  },
  // Debug colors for PDF layout visualization (always light mode)
  debugColors: {
    fixedHeader: "#ffcccc", // Light red
    headerWrapper: "#ffffcc", // Light yellow
    contentWrapper: "#ccffcc", // Light green
    tableRow: "#ccccff", // Light blue
    tableCell: "#ffccff", // Light magenta
    margins: "#ffcc99", // Light orange
    titleCell: "#99ffff", // Light cyan
    headerCell: "#99ccff", // Light sky blue
    sectionCell: "#ffff99", // Bright yellow
    numberCell: "#ff99cc", // Light pink
    grey: "#ccc", // Standard grey
  },
  // Chart of Accounts specific colors
  chartOfAccounts: {
    editModeContainer: "#E8F1F8", // Light blue-grey for edit mode container
    editModeContainerBorder: "#5382A6", // Blue border for edit mode container
    groupBackground: "#F5F9FB", // Very light blue-grey for group backgrounds
    newGroupDropZone: "#E8F1F8", // Light blue for new group drop zone
    chipPrimary: "#2B2520", // Dark text color for primary chips in light mode
    chipSecondary: "#5A6C7D", // Medium blue-grey for secondary chips in light mode
    switchColor: "#DFE633", // Primary color for switch in light mode
  },
};

const darkPalette = {
  primary: {
    main: "#DFE633", // Same primary color works well in dark mode
    light: "#E8ED5C", // Slightly brighter for dark backgrounds
    dark: "#B8C428", // Slightly darker but still vibrant
    contrastText: "#000000",
  },
  secondary: {
    main: "#7BA3C4", // Brighter blue for better dark mode visibility
    light: "#9BC4E2", // Even lighter blue for dark mode
    dark: "#5382A6", // Original blue for contrast
    contrastText: "#ffffff",
  },
  tertiary: {
    main: "#E8F4F8", // Light blue-grey to complement the palette
    light: "#F5F9FB",
    dark: "#B8D4E0",
    contrastText: "#000000",
  },
  background: {
    default: "#121212",
    paper: "#1e1e1e",
    dark: "rgba(255, 255, 255, 0.05)",
    navigation: "#0F0D0A", // Even darker brown for dark theme navigation
    header: "#1a1a1a", // Dark header background for dark theme
  },
  text: {
    primary: "#ffffff",
    secondary: "rgba(255, 255, 255, 0.7)",
    disabled: "rgba(255, 255, 255, 0.5)",
    navigation: "#ffffff", // White text for dark navigation
    navigationSecondary: "rgba(255, 255, 255, 0.8)", // Slightly transparent white
    darkHeader: "#ffffff", // White text for dark headers
  },
  divider: "rgba(255, 255, 255, 0.12)",
  grey: {
    50: "#212121",
    100: "#2d2d2d", // Slightly lighter than background for alternating table rows
    200: "#424242",
    300: "#616161",
    400: "#757575",
    500: "#9e9e9e",
    600: "#bdbdbd",
    700: "#e0e0e0",
    800: "#eeeeee",
    900: "#1a1a1a", // Very dark for transaction entry backgrounds
  },
  error: {
    main: "#f44336",
    light: "#ff7961",
    dark: "#ba000d",
  },
  warning: {
    main: "#ffa726",
    light: "#ffb955",
    dark: "#c77800",
  },
  info: {
    main: "#29b6f6",
    light: "#73e8ff",
    dark: "#0086c3",
  },
  success: {
    main: "#66bb6a",
    light: "#98ee99",
    dark: "#338a3e",
  },
  // Debug colors for PDF layout visualization (always light mode)
  debugColors: {
    fixedHeader: "#ffcccc", // Light red
    headerWrapper: "#ffffcc", // Light yellow
    contentWrapper: "#ccffcc", // Light green
    tableRow: "#ccccff", // Light blue
    tableCell: "#ffccff", // Light magenta
    margins: "#ffcc99", // Light orange
    titleCell: "#99ffff", // Light cyan
    headerCell: "#99ccff", // Light sky blue
    sectionCell: "#ffff99", // Bright yellow
    numberCell: "#ff99cc", // Light pink
    grey: "#ccc", // Standard grey
  },
  // Chart of Accounts specific colors
  chartOfAccounts: {
    editModeContainer: "#2d3748", // Dark grey for edit mode container
    editModeContainerBorder: "#4a5568", // Darker grey border for edit mode container
    groupBackground: "#2a2e3a", // Much darker grey for group backgrounds
    newGroupDropZone: "#2d3748", // Dark grey for new group drop zone
    chipPrimary: "#DFE633", // Bright primary color for chips in dark mode
    chipSecondary: "#7BA3C4", // Bright secondary color for chips in dark mode
    switchColor: "#7BA3C4", // Blue color for switch in dark mode
  },
};

// Gradient colors for buttons (shared between themes)
const gradients = {
  primary: ["#DFE633", "#B8C428"], // Primary yellow-green gradient
  secondary: ["#5382A6", "#3A5B7A"], // Secondary blue gradient
  vibrant: ["#DFE633", "#5382A6"], // Primary to secondary gradient
  create: ["#5382A6", "#7BA3C4"], // Blue to light blue
  twoTone: {
    base: ["#5382A6", "#3A5B7A"], // Blue gradient
    overlay: ["#DFE633", "#B8C428"], // Yellow-green gradient
  },
};

// Common typography settings
const typography = {
  fontFamily: [
    "MontserratReg",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
  h1: {
    fontFamily: "MontserratBold",
    fontSize: "2.5rem",
    fontWeight: 700,
  },
  h2: {
    fontFamily: "MontserratBold",
    fontSize: "2.2rem",
    fontWeight: 700,
  },
  h3: {
    fontFamily: "MontserratBold",
    fontSize: "1.8rem",
    fontWeight: 700,
  },
  h4: {
    fontFamily: "MontserratSemiBold",
    fontSize: "1.5rem",
    fontWeight: 600,
  },
  h5: {
    fontFamily: "MontserratSemiBold",
    fontSize: "1.25rem",
    fontWeight: 600,
  },
  h6: {
    fontFamily: "MontserratSemiBold",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  body1: {
    fontFamily: "MontserratReg",
    fontSize: "1rem",
  },
  body2: {
    fontFamily: "MontserratReg",
    fontSize: "0.875rem",
  },
  subtitle1: {
    fontFamily: "MontserratMed",
    fontSize: "1.1rem",
    fontWeight: 500,
  },
  subtitle2: {
    fontFamily: "MontserratMed",
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  button: {
    fontFamily: "MontserratSemiBold",
    fontWeight: 600,
    textTransform: "none",
  },
  caption: {
    fontFamily: "MontserratLight",
    fontSize: "0.75rem",
    fontWeight: 300,
  },
  overline: {
    fontFamily: "MontserratReg",
    fontSize: "0.75rem",
    textTransform: "uppercase",
  },
};

// Common component overrides
const getComponents = (mode) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "0.6rem",
        padding: "0.4rem 1.5rem",
        fontSize: "1rem",
        transition: "all 0.3s ease-in-out",
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        },
      },
      // DESIGN RULE (light mode): the primary lime (#DFE633) is unreadable on
      // white, so any control whose COLOR comes from primary — text/outlined
      // buttons and focused form fields — uses the secondary blue in light mode
      // instead. Fully-filled (contained) primary buttons keep the lime fill
      // (their text is dark, so they read fine) and dark mode is unchanged.
      ...(mode === "light" && {
        textPrimary: {
          color: lightPalette.secondary.main,
          "&:hover": { backgroundColor: "rgba(83, 130, 166, 0.08)" },
        },
        outlinedPrimary: {
          color: lightPalette.secondary.main,
          borderColor: lightPalette.secondary.main,
          "&:hover": {
            borderColor: lightPalette.secondary.dark,
            backgroundColor: "rgba(83, 130, 166, 0.08)",
          },
        },
      }),
    },
  },
  // Same light-mode rule for icon-only buttons whose color is primary.
  MuiIconButton: {
    styleOverrides: {
      colorPrimary:
        mode === "light" ? { color: lightPalette.secondary.main } : {},
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "0.5rem",
        },
      },
    },
  },
  // Focused field outline → secondary blue in light mode (default is the
  // primary lime, which is invisible on a white field). Dark mode unchanged.
  MuiOutlinedInput: {
    styleOverrides: {
      root:
        mode === "light"
          ? {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: lightPalette.secondary.main,
              },
            }
          : {},
    },
  },
  // MUI X date pickers render their own outlined input (MuiPickersOutlinedInput,
  // not MuiOutlinedInput), so they need the same focused-outline rule separately.
  // "&&" + :not(.Mui-error) out-specifies the picker's internal color variant,
  // which otherwise wins regardless of style injection order.
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root:
        mode === "light"
          ? {
              "&&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline":
                {
                  borderColor: lightPalette.secondary.main,
                },
            }
          : {},
    },
  },
  // Focused field label → secondary blue in light mode (matches the outline).
  // "&&" keeps this ahead of FormLabel's per-color focused variant even when a
  // lazily-loaded chunk (e.g. the date pickers) injects its styles after ours.
  MuiInputLabel: {
    styleOverrides: {
      root:
        mode === "light"
          ? { "&&.Mui-focused": { color: lightPalette.secondary.main } }
          : {},
    },
  },
  // Same light-mode rule for the "on" affordance of switches: the checked thumb
  // + track use secondary blue instead of the primary lime. Dark mode default.
  MuiSwitch: {
    styleOverrides: {
      root:
        mode === "light"
          ? {
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: lightPalette.secondary.main,
                "&:hover": { backgroundColor: "rgba(83, 130, 166, 0.08)" },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: lightPalette.secondary.main,
              },
            }
          : {},
    },
  },
  // Selected segment of a segmented control → secondary tint in light mode
  // (default primary lime reads as an unfilled selection on white); primary
  // lime tint in dark mode (MUI's default selected state is a neutral grey
  // that doesn't read as the accent). Centralised here so every
  // SegmentedControl inherits it without inline styling.
  MuiToggleButton: {
    styleOverrides: {
      root:
        mode === "light"
          ? {
              "&.Mui-selected": {
                backgroundColor: "rgba(83, 130, 166, 0.08)",
                color: "#33546b",
                "&:hover": { backgroundColor: "rgba(83, 130, 166, 0.16)" },
              },
            }
          : {
              "&.Mui-selected": {
                backgroundColor: "rgba(223, 230, 51, 0.12)",
                color: darkPalette.primary.main,
                "&:hover": { backgroundColor: "rgba(223, 230, 51, 0.2)" },
              },
            },
    },
  },
  // Same light-mode rule for a checked checkbox: secondary blue tick.
  MuiCheckbox: {
    styleOverrides: {
      colorPrimary:
        mode === "light"
          ? { "&.Mui-checked": { color: lightPalette.secondary.main } }
          : {},
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: "1rem",
        boxShadow:
          mode === "light"
            ? "0 2px 8px rgba(0, 0, 0, 0.1)"
            : "0 2px 8px rgba(0, 0, 0, 0.3)",
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: "1rem",
        boxShadow:
          mode === "light"
            ? "0 2px 8px rgba(0, 0, 0, 0.1)"
            : "0 2px 8px rgba(0, 0, 0, 0.3)",
      },
    },
  },
  MuiModal: {
    styleOverrides: {
      root: {
        "& .MuiBackdrop-root": {
          backgroundColor:
            mode === "light" ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.8)",
        },
      },
    },
  },
  // Let crowded footer button rows wrap to a second line instead of pushing the
  // right-most button past the dialog edge on narrow viewports. No effect when
  // the buttons already fit (desktop widths unchanged).
  MuiDialogActions: {
    styleOverrides: {
      root: {
        flexWrap: "wrap",
        rowGap: "0.5rem",
      },
    },
  },
  // Same wrap-instead-of-clip safety net for MUI Toolbars. The app's main nav is
  // a custom styled div (not a MUI Toolbar), so this only touches the handful of
  // content toolbars and never reflows the global navigation.
  MuiToolbar: {
    styleOverrides: {
      root: {
        flexWrap: "wrap",
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor:
          mode === "light"
            ? "rgba(97, 97, 97, 0.92)"
            : "rgba(33, 28, 22, 0.95)",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontFamily: "MontserratReg",
        borderRadius: "8px",
        padding: "8px 12px",
        boxShadow:
          mode === "light"
            ? "0 2px 8px rgba(0, 0, 0, 0.15)"
            : "0 2px 8px rgba(0, 0, 0, 0.4)",
      },
      arrow: {
        color:
          mode === "light"
            ? "rgba(97, 97, 97, 0.92)"
            : "rgba(33, 28, 22, 0.95)",
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        color: mode === "dark" ? "#ffffff" : undefined,
      },
      h1: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 700,
      },
      h2: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 700,
      },
      h3: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 700,
      },
      h4: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 600,
      },
      h5: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 600,
      },
      h6: {
        color: mode === "dark" ? "#ffffff" : "#2B2520",
        fontWeight: 600,
      },
      body1: {
        color: mode === "dark" ? "rgba(255, 255, 255, 0.87)" : "#2B2520",
      },
      body2: {
        color: mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#5A6C7D",
      },
    },
  },
});

// Create light theme
export const lightTheme = createTheme({
  palette: {
    mode: "light",
    ...lightPalette,
    gradients, // Add gradients to palette
  },
  typography,
  components: getComponents("light"),
  shape: {
    borderRadius: 10,
  },
  spacing: 8,
});

// Create dark theme
export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    ...darkPalette,
    gradients, // Add gradients to palette
  },
  typography: {
    ...typography,
    allVariants: {
      color: darkPalette.text.primary,
    },
  },
  components: getComponents("dark"),
  shape: {
    borderRadius: 10,
  },
  spacing: 8,
});

// Export theme utilities
export const getTheme = (mode) => (mode === "dark" ? darkTheme : lightTheme);

export default { lightTheme, darkTheme };
